import { Box, Grid, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { StyledTable } from '../../components';
import { useMessageContextManager } from '../../components/MessageOverlay/MessageOverlay';
import { ERROR_MESSAGE, LOADING_MESSAGE, OPERATIONS, REDIRECT_URLS } from '../../constants';
import { useResolutionScale } from '../../hooks';
import { updateOperations } from '../../redux/actionCreators/operations';
import {
	dropTransferLine,
	fetchAvailableTransferBJCs,
	updateCurrentLineItem,
	checkTransferLocation
} from '../../redux/actionCreators/station';
import { getNextOperation } from '../../utils/operationFlow';
import { UpdateScanVIN } from '../ScanVin/ScanVin';

const transferBJCColumns = [
	{
		width: 220,
		title: 'ASN',
		field: 'asn',
		align: 'center',
		enableSearch: false,
		enableFilter: false,
		render: (row) => row?.asn ?? '—',
	},
	{
		width: 300,
		title: 'SERIAL NUMBER (VIN)',
		field: 'serialNumber',
		align: 'center',
		enableSearch: false,
		enableFilter: false,
		render: (row) => row?.serialNumber ?? '—',
	},
	{
		width: 220,
		title: 'PART NUMBER',
		field: 'partNumber',
		align: 'center',
		enableSearch: false,
		enableFilter: false,
		render: (row) => row?.partNumber ?? '—',
	},
];

const mapDispatchToProps = (dispatch) => ({
	fetchAvailableTransferBJCs: (successCallback, errorCallback) =>
		dispatch(fetchAvailableTransferBJCs(successCallback, errorCallback)),
	dropTransferLine: (asn, successCallback, errorCallback) =>
		dispatch(dropTransferLine(asn, successCallback, errorCallback)),
	updateCurrentLineItem: (currentLineItem) =>
		dispatch(updateCurrentLineItem(currentLineItem)),
	updateOperations: (data) => dispatch(updateOperations(data)),
	checkTransferLocation: (serialNumber, successCallback, errorCallback) =>
		dispatch(
			checkTransferLocation(
				serialNumber,
				successCallback,
				errorCallback,
			),
		),
});

const mapStateToProps = (state) => ({
	themeMode: state.theme.themeMode,
	allowedOperations: state.operations.allowedOperations,
});

export const TransferDrop = ({
	themeMode,
	fetchAvailableTransferBJCs,
	dropTransferLine,
	updateCurrentLineItem,
	checkTransferLocation,
	updateOperations,
	allowedOperations,
}) => {
	const navigate = useNavigate();
	const { showMessageOverlay } = useMessageContextManager();
	const { scaleValue } = useResolutionScale();

	const [transferBJCs, setTransferBJCs] = useState([]);

	const loadTransferBJCs = useCallback(() => {
		fetchAvailableTransferBJCs(
			(data) => {
				const list = Array.isArray(data) ? data : [];
				setTransferBJCs(list.map((item, idx) => ({ ...item, id: item.asn ?? idx })));
			},
			() => {
				setTransferBJCs([]);
			},
		);
	}, [fetchAvailableTransferBJCs]);

	useEffect(() => {
		loadTransferBJCs();
	}, [loadTransferBJCs]);

	const handleDrop = (_, scannedValue, resetBarcodeValue) => {
			const vin = scannedValue.substring(0, 17);

			const match = transferBJCs.find((bjc) => bjc.serialNumber === vin);

			// VIN not present in available transfer list
			if (!match) {
				const closeMessageOverlay = showMessageOverlay({
					contextText: LOADING_MESSAGE.CHECKING_VIN_AVAILABLITY,
					loading: true,
				});

				checkTransferLocation(
					vin,
					() => {
						showMessageOverlay({
							contextText: `${ERROR_MESSAGE.ERROR_CONTEXT_TEXT}VIN Not Found`,
							info: 'Vehicle is not available at child line',
							error: true,
						});

						resetBarcodeValue();
					},
					(error) => {
						console.error('Vehicle location message:', error);
						showMessageOverlay({
							contextText: `${ERROR_MESSAGE.ERROR_CONTEXT_TEXT}VIN Not Found`,
							info:
								error?.response?.data ||
								error?.response?.data?.message ||
								'Unable to validate vehicle location.',
							error: true,
						});

						resetBarcodeValue();
					},
				);

				return;
			}

		const closeMessageOverlay = showMessageOverlay({
			contextText: LOADING_MESSAGE.CHECKING_VIN_AVAILABLITY,
			loading: true,
		});

		dropTransferLine(
			match.asn,
			() => {
				closeMessageOverlay();
				resetBarcodeValue();

				const updatedLineItem = {
					sequenceNumber: match.asn,
					serialNumber: vin,
					partNumber: match.partNumber,
					completedOperations: [OPERATIONS.TRANSFER_DROP],
				};
				updateCurrentLineItem(updatedLineItem);

				const nextOperation = getNextOperation(
					[OPERATIONS.TRANSFER_DROP],
					allowedOperations,
				);
				updateOperations({ currentOperation: nextOperation });

				const nextPath = REDIRECT_URLS[nextOperation] || '/transfer-drop';
				navigate(nextPath);
			},
			(error) => {
				closeMessageOverlay();
				showMessageOverlay({
					contextText: `${ERROR_MESSAGE.ERROR_CONTEXT_TEXT}Transfer Drop Failed`,
					info: error?.response?.data || error?.response?.data?.message || 'Failed to drop transfer vehicle.',
					error: true,
				});
				resetBarcodeValue();
			},
		);
	};

	return (
		<Grid container sx={{ paddingTop: 0, marginTop: 0 }}>
			<Grid item xs={12}>
				<Grid
					container
					direction="row"
					justifyContent="space-between"
					sx={{ paddingTop: 0, marginTop: 0, gap: 0 }}
				>
					<Grid item xs={12} sx={{ paddingTop: 0, marginTop: 0 }}>
						<Grid container alignItems="center" flex={1} justifyContent="space-between">
							<Grid item>
								<Typography
									variant="inherit"
									sx={{ fontWeight: 600, fontSize: `${scaleValue(24)}px` }}
								>
									Available Transfer Vehicles ({transferBJCs.length})
								</Typography>
							</Grid>
						</Grid>

						<StyledTable
							rows={transferBJCs}
							columns={transferBJCColumns}
							showCheckBox={false}
							elevated={true}
							isRowClickCopy={true}
						/>
					</Grid>

					<Grid item xs={12} marginTop={2}>
						<UpdateScanVIN
							selectedRows={[]}
							selectedRowObjects={[]}
							themeMode={themeMode}
							handleDrop={handleDrop}
						/>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};

export default connect(mapStateToProps, mapDispatchToProps)(TransferDrop);
